import classNames from 'classnames';
import type { CountryCode, E164Number } from 'libphonenumber-js';
import { FocusEvent, useRef } from 'react';
import { FieldError } from 'react-hook-form';
import { getCountries } from 'react-phone-number-input';
import PhoneInput from 'react-phone-number-input/input';

import InputError from '../InputError';
import CountrySelect from './CountrySelect';

type PhoneInputProps = {
  defaultCountry: CountryCode;
  name: string;
  value?: E164Number | string;
  selectedCountry: CountryCode;
  isDirty: boolean;
  errors: FieldError | undefined;
  required?: boolean;
  label: string;
  numberLabel: string;
  countryCodeLabel: string;
  onChange: (value: E164Number) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  changeSelectedCountry: (country: CountryCode) => void;
  i18nFunction: (key: string) => string;
};

export default function CustomPhoneInput({
  label,
  numberLabel,
  countryCodeLabel,
  defaultCountry,
  name,
  value,
  onChange,
  onBlur,
  selectedCountry,
  changeSelectedCountry,
  isDirty,
  errors,
  required,
  i18nFunction,
}: PhoneInputProps) {
  const phoneInputRef = useRef(null);

  const countries = getCountries();

  const changeCountry = (country: CountryCode) => {
    changeSelectedCountry(country);
    if (phoneInputRef.current)
      (phoneInputRef.current as HTMLInputElement)?.focus();
  };

  return (
    <fieldset className="flex w-full flex-col gap-2">
      <legend className="mb-2">
        {label}
        {required && <span aria-hidden="true">{' *'}</span>}
      </legend>
      <div className="flex flex-wrap gap-2">
        <label htmlFor={`${name}-country-code`} className="sr-only">
          {countryCodeLabel}
        </label>
        <CountrySelect
          name={`${name}-country-code`}
          selected={selectedCountry}
          options={countries}
          onChange={changeCountry}
          i18nFunction={i18nFunction}
          ariaLabel={countryCodeLabel}
        />
        <div className="relative w-full flex-1 gap-1">
          <label htmlFor={name} className="sr-only">
            {numberLabel}
          </label>
          <PhoneInput
            id={name}
            name={name}
            defaultCountry={defaultCountry}
            country={selectedCountry}
            required={required}
            aria-required={required}
            className={classNames(
              { 'focus-input-error': errors?.message },
              'input border-accent focus-input w-full rounded-lg border-2 text-black focus:ring-offset-white',
            )}
            value={value as E164Number}
            onChange={onChange}
            ref={phoneInputRef}
            onBlur={onBlur}
            data-testid="phone"
          />

          <InputError isDirty={isDirty} errors={errors} />
        </div>
        {errors?.message && (
          <div className="w-full rounded-lg bg-[#FFE2E0] p-3 text-xs leading-6 text-black">
            {errors?.message}
          </div>
        )}
      </div>
    </fieldset>
  );
}

CustomPhoneInput.displayName = 'CustomPhoneInput';
